import { Pie, PieChart, type LegendProps, type TooltipProps } from "recharts";
import useQuestionResponse from "../../../Responses/useQuestionResponse";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import getChartColor from "./getChartColor";
import { selectedOptionIndexes } from "./answerOptions";
import { ChartContainer, ChartLegend, ChartTooltip } from "frontend-components";

type ChartDataEntry = {
    name: string | number;
    value: number;
    fill: string;
};

function CustomChartTooltip(props: TooltipProps<number, string>) {
    const { payload } = props;

    const optionText = payload?.[0]?.payload.name;

    const { i18n, t } = useTranslation();
    return (
        <div className="bg-container border-container-outline border rounded p-2 text-xs">
            <div className="flex items-center gap-2">
                <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: payload?.[0]?.payload.fill }}
                />
                <h1 className="text-sm font-semibold">{optionText}</h1>
            </div>
            <h2 className="font-semibold mb-1 mt-2">
                {t("common.count")}: {payload?.[0]?.value?.toLocaleString(i18n.language)}
            </h2>
        </div>
    );
}

function CustomChartLegend(props: LegendProps) {
    const { payload } = props;

    return (
        <div className="w-48 min-w-0 lg:w-60 xl:w-80">
            {payload?.map((entry, index) => {
                return (
                    <div key={`item-${index}`} className="mb-1 flex min-w-0 items-center gap-2">
                        <div
                            className="size-2 shrink-0 rounded-full"
                            style={{ backgroundColor: entry.color || "transparent" }}
                        />
                        <span className="min-w-0 flex-1 truncate text-sm font-light">
                            {entry.value}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function MobileChartLegend({ chartData }: { chartData: ChartDataEntry[] }) {
    return (
        <div className="w-full min-w-0 space-y-1">
            {chartData.map((entry, index) => (
                <div key={`${entry.name}-${index}`} className="flex min-w-0 items-start gap-2">
                    <div
                        className="mt-1.5 size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: entry.fill }}
                    />
                    <span className="min-w-0 flex-1 wrap-break-word text-sm font-light">
                        {entry.name}
                    </span>
                </div>
            ))}
        </div>
    );
}

export default function MultipleChoicePieChart() {
    const { question, answers } = useQuestionResponse();

    const chartData = useMemo(() => {
        const highestQuestionOptionValue = question.questionOptions?.length || 0;

        const options = Array.from({ length: highestQuestionOptionValue })
            .fill(0)
            .map((_, idx) => ({ option: idx, count: 0 }));

        answers.forEach(({ answerValue }) => {
            selectedOptionIndexes(answerValue).forEach((index) => {
                if (options[index]) {
                    options[index].count += 1;
                }
            });
        });

        return options
            .filter(({ count }) => count > 0)
            .map(({ option, count }, idx) => ({
                name: question.questionOptions?.[option] || option,
                value: count,
                fill: getChartColor(idx),
            }));
    }, [answers, question]);

    return (
        <div className="min-w-0 flex-1 pb-0 lg:ml-10">
            <div className="flex min-w-0 flex-col gap-4 sm:hidden">
                <MobileChartLegend chartData={chartData} />
                <ChartContainer className="mx-auto h-[180px] w-full min-w-0" config={{}}>
                    <PieChart>
                        <ChartTooltip content={<CustomChartTooltip />} />
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={70}
                            outerRadius={80}
                        />
                    </PieChart>
                </ChartContainer>
            </div>
            <ChartContainer
                className="mx-auto hidden h-[220px] min-w-0 w-full sm:flex sm:min-w-max"
                config={{}}
            >
                <PieChart>
                    <ChartLegend
                        align="left"
                        layout="vertical"
                        verticalAlign="middle"
                        content={<CustomChartLegend />}
                    />
                    <ChartTooltip content={<CustomChartTooltip />} />
                    <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={70}
                        outerRadius={80}
                    />
                </PieChart>
            </ChartContainer>
        </div>
    );
}
